import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Box, CloseButton, Dialog, HStack, Portal, chakra, useBreakpointValue } from '@chakra-ui/react';
import { IconGlyph } from './icon_glyph.tsx';

// The app's dialogs (Chakra's Dialog): a title, a body, buttons at the bottom; closed by the cross,
// a tap beside it or Escape, unless the dialog asks a question that must be answered; not by focus
// moving out of it.
//
// A dialog keeps a stack of screens, as iOS pushes them (the owner, 2026-10-16: a field's options
// open "a new screen with all the options"; sub-forms "like a wizard", spec §5.6): the top screen's
// title with a way back in the header, the screen in the body, no buttons at the bottom and no
// cross (issue #34: the cross on a screen threw away the dialog's changes). What is below stays
// mounted, hidden, so that it keeps its state and its scroll position. A field pushes its screen
// with `useDialogScreen` and draws into the slot it gets, to any depth.

interface ScreenEntry {
    readonly id: string;
    readonly title: string;
    readonly onBack: () => void;
    /** The body's scroll position below this screen, given back when it goes. */
    readonly scrollBelow: number;
}

interface DialogScreens {
    readonly stack: readonly ScreenEntry[];
    push(id: string, title: string, onBack: () => void) : void;
    pop(id: string) : void;
    /** Where a screen's content goes (a portal target); null until it is drawn. */
    slotOf(id: string) : HTMLElement | null;
}

const DialogScreenContext = createContext<DialogScreens | null>(null);

/**
 * A screen of this component's own while `isOpen`, on the stack of the dialog it is in: the slot
 * to draw it into. Outside a dialog, `isInDialog` is false and the caller opens a dialog instead.
 */
export function useDialogScreen(isOpen: boolean, title: string, onBack: () => void) : { isInDialog: boolean; slot: HTMLElement | null } {
    const screens = useContext(DialogScreenContext);
    const id = useId();
    const latestOnBack = useRef(onBack);
    latestOnBack.current = onBack;
    const push = screens?.push;
    const pop = screens?.pop;

    useEffect(() => {
        if (!isOpen || !push || !pop) {
            return undefined;
        }

        push(id, title, () => latestOnBack.current());

        return () => pop(id);
    }, [isOpen, push, pop, id, title]);

    return { isInDialog: screens !== null, slot: isOpen && screens ? screens.slotOf(id) : null };
}

export interface ChromeDialogProps {
    title: string;
    onClose: () => void;
    children: ReactNode;
    /** The buttons at the bottom. */
    footer?: ReactNode;
    /** A question must be answered by its buttons: no cross, no closing beside it. */
    isQuestion?: boolean;
    size?: 'sm' | 'md' | 'lg';
    /**
     * Whether the first field takes the focus when the dialog opens (the default). Off for a form
     * whose fields are filled in already: on a tablet the keyboard would come up and cover the dialog.
     */
    focusesField?: boolean;
    /** Fills the screen on a phone (below `md`, as the phone's menu), for the big editing dialogs (issue #34). */
    fillsPhone?: boolean;
}

interface UncoveredArea {
    readonly top: number;
    readonly height: number;
}

/**
 * The part of the page that is not under the on-screen keyboard (the visual viewport), while the
 * keyboard is up; undefined otherwise. iOS does not shrink the page for its keyboard: a dialog
 * centred on the page has its buttons behind the keyboard (Mac checks of E5, E6a, 2026-10-17).
 */
function useUncoveredArea() : UncoveredArea | undefined {
    const [area, setArea] = useState<UncoveredArea>();

    useEffect(() => {
        const viewport = window.visualViewport;
        if (!viewport) {
            return undefined;
        }

        const measure = () : void => {
            const isCovered = viewport.height < window.innerHeight - 1 || viewport.offsetTop > 1;
            setArea((current) => {
                if (!isCovered) {
                    return undefined;
                }
                if (current && current.top === viewport.offsetTop && current.height === viewport.height) {
                    return current;
                }

                return { top: viewport.offsetTop, height: viewport.height };
            });
        };
        measure();
        viewport.addEventListener('resize', measure);
        viewport.addEventListener('scroll', measure);

        return () => {
            viewport.removeEventListener('resize', measure);
            viewport.removeEventListener('scroll', measure);
        };
    }, []);

    return area;
}

const BackButton = chakra('button', {
    base: { display: 'flex', alignItems: 'center', gap: '1', color: 'blue.400', cursor: 'pointer', px: '1', py: '1', ms: '-1', borderRadius: 'md', flex: 'none' },
});

export function ChromeDialog({ title, onClose, children, footer, isQuestion = false, size = 'md', focusesField = true, fillsPhone = false }: ChromeDialogProps) : ReactNode {
    const content = useRef<HTMLDivElement>(null);
    const body = useRef<HTMLDivElement>(null);
    const [stack, setStack] = useState<readonly ScreenEntry[]>([]);
    // A screen keeps the dialog at least as tall as the content was, so that it does not jump.
    const [screenMinHeight, setScreenMinHeight] = useState<number>();
    // The screens' slots, with ref callbacks that keep their identity (a new one would detach and
    // attach the element on every render).
    const slots = useRef(new Map<string, HTMLDivElement>());
    const slotRefs = useRef(new Map<string, (element: HTMLDivElement | null) => void>());
    const [slotsVersion, setSlotsVersion] = useState(0);
    const restoreScroll = useRef<number | undefined>(undefined);
    const uncovered = useUncoveredArea();
    // While the keyboard is up, the dialog fits in what it leaves, with narrow margins; its top
    // stays below the status bar, which iOS draws over the page (and the window's buttons, index.css).
    const topMargin = 'max(0.5rem, var(--casa-top-inset, 0px))';
    const maxHeight = uncovered ? `calc(100% - 0.5rem - ${topMargin})` : 'calc(100% - 7.5rem)';
    const isFull = (useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false) && fillsPhone;

    // The dialog is shorter once the keyboard is up: the focused field is brought back into view,
    // above the buttons (the Mac's check of 2026-10-17: Token behind the button row).
    useLayoutEffect(() => {
        const focused = document.activeElement;
        if (!uncovered || !(focused instanceof HTMLElement) || !content.current?.contains(focused)) {
            return;
        }

        focused.scrollIntoView({ block: 'nearest' });
    }, [uncovered]);

    const refFor = (id: string) : ((element: HTMLDivElement | null) => void) => {
        let ref = slotRefs.current.get(id);
        if (!ref) {
            ref = (element) => {
                if (element) {
                    slots.current.set(id, element);
                    setSlotsVersion((version) => version + 1);
                } else {
                    slots.current.delete(id);
                }
            };
            slotRefs.current.set(id, ref);
        }

        return ref;
    };

    const push = useCallback((id: string, screenTitle: string, onBack: () => void) : void => {
        const scrollBelow = body.current?.scrollTop ?? 0;
        setStack((current) => {
            if (current.length === 0) {
                setScreenMinHeight(content.current?.offsetHeight);
            }

            return [...current.filter((entry) => entry.id !== id), { id, title: screenTitle, onBack, scrollBelow }];
        });
    }, []);
    const pop = useCallback((id: string) : void => {
        setStack((current) => {
            const entry = current.find((each) => each.id === id);
            if (!entry) {
                return current;
            }

            restoreScroll.current = entry.scrollBelow;
            slotRefs.current.delete(id);

            return current.filter((each) => each.id !== id);
        });
    }, []);
    const slotOf = useCallback((id: string) : HTMLElement | null => slots.current.get(id) ?? null, []);
    // A new value when a screen's element attaches, so that its field draws into it then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const screens = useMemo<DialogScreens>(() => ({ stack, push, pop, slotOf }), [stack, push, pop, slotOf, slotsVersion]);

    const top = stack.at(-1);

    // Escape on a screen goes back one screen, not out of the dialog: caught on the window, before
    // the dialog's own listener on the document sees it.
    useEffect(() => {
        if (!top) {
            return undefined;
        }

        const onKeyDown = (event: KeyboardEvent) : void => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                top.onBack();
            }
        };
        window.addEventListener('keydown', onKeyDown, { capture: true });

        return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
    }, [top]);

    // A new screen starts at its top; going back returns to where the screen below was left.
    const topId = top?.id;
    useLayoutEffect(() => {
        if (!body.current) {
            return;
        }
        if (restoreScroll.current !== undefined) {
            body.current.scrollTop = restoreScroll.current;
            restoreScroll.current = undefined;
        } else if (topId !== undefined) {
            body.current.scrollTop = 0;
        }
    }, [topId]);

    return (
        <Dialog.Root
            open
            size={isFull ? 'full' : size}
            placement="center"
            scrollBehavior="inside"
            // Never a tap beside the dialog: one by accident closed a tile's dialog and lost its
            // changes (issue #16); the same for every dialog (the owner).
            closeOnInteractOutside={false}
            closeOnEscape={!isQuestion}
            {...(focusesField ? {} : { initialFocusEl: () => content.current })}
            // Only the cross, its buttons or Escape close it, never focus moving away: a dialog that
            // closes hands focus back to what opened it, outside the next one, which then closed
            // itself on computers without a touchscreen (adding a tile, 2026-10-17).
            onFocusOutside={(event) => event.preventDefault()}
            onOpenChange={(details) => {
                if (!details.open) {
                    onClose();
                }
            }}
        >
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner className="chrome" style={uncovered ? { top: uncovered.top, height: uncovered.height } : undefined}>
                    {/* The width is set, not left to the content, so that a screen with wide content
                        does not widen the dialog over its margins (the iPhone, 2026-10-17). Full on a
                        phone, it fills what the keyboard leaves, its content clear of the status bar,
                        the home indicator and the notch. */}
                    <Dialog.Content
                        ref={content}
                        {...(isFull
                            ? {
                                width: '100%',
                                height: '100%',
                                minH: '0',
                                maxH: '100%',
                                pt: 'var(--casa-top-inset)',
                                pb: uncovered ? undefined : 'env(safe-area-inset-bottom)',
                                ps: 'env(safe-area-inset-left)',
                                pe: 'env(safe-area-inset-right)',
                            }
                            : {
                                mx: '4',
                                width: 'calc(100% - 2rem)',
                                maxH: maxHeight,
                                mt: uncovered ? topMargin : undefined,
                                mb: uncovered ? '2' : undefined,
                                minH: top && screenMinHeight !== undefined ? `min(${screenMinHeight}px, ${maxHeight})` : undefined,
                            })}
                    >
                        <Dialog.Header>
                            {top ? (
                                <HStack gap="2" minW="0">
                                    <BackButton type="button" aria-label="Back" onClick={top.onBack}>
                                        <Box transform="rotate(180deg)">
                                            <IconGlyph name="chevron-right" size={16} color="#60a5fa" />
                                        </Box>
                                    </BackButton>
                                    <Dialog.Title truncate>{top.title}</Dialog.Title>
                                </HStack>
                            ) : (
                                <Dialog.Title>{title}</Dialog.Title>
                            )}
                        </Dialog.Header>
                        <Dialog.Body ref={body}>
                            <DialogScreenContext.Provider value={screens}>
                                <Box display={top ? 'none' : undefined}>{children}</Box>
                            </DialogScreenContext.Provider>
                            {stack.map((entry) => (
                                <Box key={entry.id} ref={refFor(entry.id)} display={entry.id === topId ? undefined : 'none'} />
                            ))}
                        </Dialog.Body>
                        {footer && !top ? <Dialog.Footer flexWrap="wrap">{footer}</Dialog.Footer> : null}
                        {isQuestion || top ? null : (
                            <Dialog.CloseTrigger asChild top={isFull ? 'calc(var(--casa-top-inset) + 0.5rem)' : undefined}>
                                <CloseButton size="sm" />
                            </Dialog.CloseTrigger>
                        )}
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
}
